import { useEffect, useRef } from 'react'

// Reusable inline text editor for the page-designer canvas. Wraps a
// contentEditable surface that:
//   • shows `placeholder` when empty (via CSS data-attribute)
//   • syncs the external `value` into the DOM only when it has actually
//     drifted, so the caret never jumps mid-typing
//   • stops mousedown/click from bubbling to the parent block (otherwise
//     selecting text inside a card would re-select the card every click)
//   • optionally suppresses Enter (so Enter doesn't insert a <br> in
//     fields like a button label or a card title)
//
// Use this anywhere a block has a small editable text field on the canvas.

interface Props {
  value: string
  onChange: (next: string) => void
  placeholder?: string
  multiline?: boolean              // when false, Enter blurs instead of inserting newline
  onFocus?: () => void
  onBlur?: () => void
  className?: string
  style?: React.CSSProperties
  ariaLabel?: string
  // When true, paste is forced to plain text (no inherited formatting
  // from the source document). Default: true.
  plainTextPaste?: boolean
}

export default function EditableText({
  value,
  onChange,
  placeholder,
  multiline = true,
  onFocus,
  onBlur,
  className,
  style,
  ariaLabel,
  plainTextPaste = true,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (el.innerText !== value) {
      // innerText keeps line breaks consistent across browsers and avoids
      // accidentally mounting HTML that the user pasted earlier.
      el.innerText = value ?? ''
    }
  }, [value])

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label={ariaLabel}
      data-placeholder={placeholder ?? ''}
      className={`pg-editable ${className ?? ''}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onFocus={onFocus}
      onBlur={() => {
        const el = ref.current
        if (el) {
          const next = el.innerText
          if (next !== value) onChange(next)
        }
        onBlur?.()
      }}
      onInput={(e) => {
        const next = (e.currentTarget as HTMLDivElement).innerText
        if (next !== value) onChange(next)
      }}
      onKeyDown={(e) => {
        if (!multiline && e.key === 'Enter') {
          e.preventDefault()
          ;(e.currentTarget as HTMLDivElement).blur()
        }
      }}
      onPaste={(e) => {
        if (!plainTextPaste) return
        e.preventDefault()
        e.stopPropagation()
        const text = e.clipboardData.getData('text/plain')
        try { document.execCommand('insertText', false, text) } catch { /* no-op */ }
      }}
      style={{
        outline: 'none',
        minHeight: '1.2em',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        ...style,
      }}
    />
  )
}
